<script setup lang="ts">
defineProps<{
  isLoading: boolean
  template: 'list' | 'map' | 'split'
}>()
const emits = defineEmits<{
  'change-template': ['list' | 'map' | 'split']
  'clear-refinements': []
  'clear-filter': []
}>()
defineSlots<{ default(): any }>()

const { locale, t } = useI18n()
const { trackSuggestion } = useSearchTracking()
const {
  fetchAllDestinations,
  setData,
  setSubmittedSearch,
  unknownDestination,
} = useSearchInput()
const { allSelectedFilters, clearAllFilters } = useSearchNoResult()

const { data: allDestinations } = await useAsyncData(
  'all-destinations',
  async () => fetchAllDestinations(),
  { default: () => [] },
)

const selectedDestination = computed(
  () =>
    unknownDestination.value?.queryName ??
    allSelectedFilters.value.find(({ name }) => name === 'location')?.text,
)

const destinations = computed(() =>
  (unknownDestination.value?.country
    ? allDestinations.value.filter(
        (d) => d.parent_id === unknownDestination.value?.country?.id,
      )
    : allDestinations.value.filter((d) => d.is_country)
  ).sort((a, b) => b.houses_count - a.houses_count),
)

const handleClickDestination = async (desti: {
  id: number
  clusterizedName: string
  isCountry: boolean
}) => {
  await clearRefinements()
  trackSuggestion(desti.isCountry ? 'country' : 'region')
  setData('location', {
    id: `${desti.id}`,
    name: desti.clusterizedName,
    type: 'destination',
  })
  setSubmittedSearch(true)
}

const clearRefinements = async () => {
  await clearAllFilters()
  emits('clear-refinements')
}

const clearFilter = (cb: () => void) => {
  cb()
  emits('clear-filter')
}
</script>

<template>
  <div
    :class="[
      'mt-6 flex items-start',
      template === 'map' ? 'justify-end' : 'justify-between',
    ]"
  >
    <div v-if="template !== 'map'" class="px-2 sm:max-w-[1080px] sm:px-0">
      <div v-if="isLoading">
        <p class="mb-1 h-6 animate-pulse rounded bg-gray-200" />
        <p class="mb-4 h-5 animate-pulse rounded bg-gray-200 text-sm" />
      </div>
      <template v-else>
        <I18nT
          class="mb-1 font-medium leading-snug"
          :keypath="`noResults.${selectedDestination ? 'withDestination' : 'title'}`"
          tag="p"
        >
          <template #destination>
            <strong>{{ selectedDestination }}</strong>
          </template>
        </I18nT>

        <I18nT class="text-sm leading-tight" keypath="noResults.text" tag="p">
          <template #resetFilters>
            <BaseButton
              class="!font-normal underline"
              color="secondary"
              variant="link"
              @click="clearRefinements"
            >
              {{ t('noResults.resetFilters') }}
            </BaseButton>
          </template>
        </I18nT>
      </template>

      <div
        v-if="allSelectedFilters.length > 1"
        class="mb-4 mt-1 inline-flex flex-wrap gap-2"
      >
        <div
          v-for="filter in allSelectedFilters"
          :key="filter.name"
          class="flex h-10 items-center justify-center gap-2 overflow-hidden rounded bg-secondary-100 py-2 pl-3 pr-2 text-sm font-medium leading-tight text-secondary-400 sm:text-md"
        >
          {{ filter.text }}
          <button
            :aria-label="`Remove filter ${filter.name}`"
            type="button"
            @click="clearFilter(filter.onRemove)"
          >
            <BaseIcon name="close" :size="1" />
          </button>
        </div>
      </div>

      <template v-if="unknownDestination">
        <h3 class="mb-4 mt-10 text-xl font-bold sm:mb-5">
          {{
            t(
              `unknownDestination.${unknownDestination?.country ? 'withCountry' : 'ourDestinations'}`,
              {
                country: unknownDestination?.country?.name[locale],
              },
            )
          }}
        </h3>

        <LazyDestinationGrid
          :class="{ 'md:grid-cols-5 md:gap-x-5': template === 'list' }"
          :destinations="destinations"
          :img-attrs="{ height: 100, width: 180 }"
          @click-destination="
            handleClickDestination({
              id: $event.id,
              clusterizedName: $event.clusterized_name[locale],
              isCountry: $event.is_country,
            })
          "
        />
        <LazySearchScheduleACall
          class="mt-10 w-full rounded bg-primary-100 p-5"
          orientation="horizontal"
          with-image
        />
      </template>

      <LazySearchScheduleACall
        v-else-if="!isLoading"
        class="mt-16 w-full rounded bg-beige p-5"
        orientation="horizontal"
        with-image
      />
    </div>

    <SearchResultHeaderTemplateSwitcher
      class="z-30 mr-4"
      :template="template"
      @change-template="$emit('change-template', $event)"
    />
  </div>
</template>

<i18n lang="yaml">
en:
  noResults:
    withDestination: '{destination}: @:noResults.title'
    title: We did not find any property matching your search.
    text: 'Try reducing or {resetFilters}, moving around the map, or exploring another destination.'
    resetFilters: resetting the filters
  filters:
    dates: '{startDate} to {endDate}'
    guests: '{n} guests'
  unknownDestination:
    ourDestinations: Our destinations
    withCountry: '@:unknownDestination.ourDestinations: {country}'
fr:
  noResults:
    withDestination: '{destination} : @:noResults.title'
    title: Nous n'avons pas trouvé de propriété correspondant à votre recherche.
    text: 'Essayez de réduire ou de {resetFilters}, de vous déplacer sur la carte, ou d’explorer une autre destination.'
    resetFilters: réinitialiser les filtres
  filters:
    dates: '{startDate} au {endDate}'
    guests: '{n} voyageurs'
  unknownDestination:
    ourDestinations: Nos destinations
    withCountry: '@:unknownDestination.ourDestinations : {country}'
</i18n>
